import { getApps, initializeApp, type FirebaseApp, type FirebaseOptions } from 'firebase/app';
import {
  ReCaptchaEnterpriseProvider,
  initializeAppCheck,
  type AppCheck,
} from 'firebase/app-check';
import { connectAuthEmulator, getAuth, type Auth } from 'firebase/auth';
import { connectFirestoreEmulator, getFirestore, type Firestore } from 'firebase/firestore';
import {
  connectFunctionsEmulator,
  getFunctions,
  type Functions,
} from 'firebase/functions';

const REGION = 'asia-northeast3';
const USE_EMULATORS = import.meta.env.DEV && import.meta.env.VITE_FIREBASE_USE_EMULATORS === '1';

let appCheck: AppCheck | null = null;
let authEmulatorConnected = false;
let firestoreEmulatorConnected = false;
let functionsEmulatorConnected = false;

function options(): FirebaseOptions {
  const config: FirebaseOptions = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
  };

  if (!config.apiKey || !config.authDomain || !config.projectId || !config.appId) {
    throw new Error('Firebase is not configured for this build.');
  }
  return config;
}

function initialiseAppCheck(app: FirebaseApp): void {
  if (appCheck) return;

  const siteKey = import.meta.env.VITE_APPCHECK_SITE_KEY;
  const debugToken = import.meta.env.VITE_APPCHECK_DEBUG_TOKEN;
  if (import.meta.env.PROD && !siteKey) {
    throw new Error('Firebase App Check is not configured for this build.');
  }
  if (!siteKey || (!import.meta.env.PROD && !debugToken)) return;

  if (import.meta.env.DEV && debugToken) {
    const target = globalThis as typeof globalThis & {
      FIREBASE_APPCHECK_DEBUG_TOKEN?: string | boolean;
    };
    target.FIREBASE_APPCHECK_DEBUG_TOKEN = debugToken;
  }

  appCheck = initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(siteKey),
    isTokenAutoRefreshEnabled: true,
  });
}

export function firebaseApp(): FirebaseApp {
  const app = getApps()[0] ?? initializeApp(options());
  initialiseAppCheck(app);
  return app;
}

export function firebaseAuth(): Auth {
  const auth = getAuth(firebaseApp());
  if (USE_EMULATORS && !authEmulatorConnected) {
    connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
    authEmulatorConnected = true;
  }
  return auth;
}

export function firebaseDb(): Firestore {
  const db = getFirestore(firebaseApp());
  if (USE_EMULATORS && !firestoreEmulatorConnected) {
    connectFirestoreEmulator(db, '127.0.0.1', 8080);
    firestoreEmulatorConnected = true;
  }
  return db;
}

export function firebaseFunctions(): Functions {
  const functions = getFunctions(firebaseApp(), REGION);
  if (USE_EMULATORS && !functionsEmulatorConnected) {
    connectFunctionsEmulator(functions, '127.0.0.1', 5001);
    functionsEmulatorConnected = true;
  }
  return functions;
}
